/**
 * Reasoning Prompts — constitutional classifier / refusal / judge prompts.
 *
 * Master-detail: ConfigKeyList (left) drives which of the 5 hardcoded
 * reasoning config keys (see reasoningManifest.ts) is shown in the editor
 * (right) — PromptEditor for the versioned prompt_body/model/temperature/
 * max_tokens fields, plus a collapsible ABTestSandbox for a live draft-vs-
 * active comparison, and a History button opening VersionHistoryDrawer.
 *
 * GET /config only returns active rows, so a key can be "not configured"
 * (no rows at all) — the editor buffer then starts from defaults and saving
 * creates v1. All writes (save, restore) hit the production Quill DB and are
 * gated behind the shared components/quill/common/QuillActionConfirm (see
 * that file for why it isn't the shared help/ActionConfirm, which is bound
 * to a content registry).
 *
 * Selection state is mirrored to `?key=` so the current config key survives
 * a refresh/share — trivial via useSearchParams, per the build brief.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography, Snackbar, Alert, CircularProgress } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import { useSearchParams } from 'react-router-dom';
import { useQuillHealth } from '../QuillHealthContext';
import { HelpTip } from '../../help/HelpTip';
import { colors, typography } from '../../../theme/designTokens';
import { quillApi, ReasoningConfigKey, ReasoningConfigRow } from '../../../services/quillAdminService';
import ConfigKeyList from './ConfigKeyList';
import PromptEditor from './PromptEditor';
import VersionHistoryDrawer from './VersionHistoryDrawer';
import ABTestSandbox from './ABTestSandbox';
import QuillActionConfirm from '../common/QuillActionConfirm';
import {
  REASONING_CONFIG_MANIFEST,
  ReasoningEditorBuffer,
  bufferFromRow,
  isBufferDirty,
  describeReasoningError,
} from './reasoningManifest';

const DEFAULT_KEY: ReasoningConfigKey = REASONING_CONFIG_MANIFEST[0].key;

type Severity = 'success' | 'error';

const ReasoningPromptsPage: React.FC = () => {
  const { status } = useQuillHealth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Only read the query param once on mount — after that, selection is driven
  // by user interaction (doSelectKey), which also keeps the URL in sync.
  const initialKey = useMemo<ReasoningConfigKey>(() => {
    const fromQuery = searchParams.get('key');
    const found = REASONING_CONFIG_MANIFEST.find((m) => m.key === fromQuery);
    return found ? found.key : DEFAULT_KEY;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [selectedKey, setSelectedKey] = useState<ReasoningConfigKey>(initialKey);

  const [configRows, setConfigRows] = useState<ReasoningConfigRow[] | null>(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [configError, setConfigError] = useState<string | null>(null);

  const [buffer, setBuffer] = useState<ReasoningEditorBuffer>(() => bufferFromRow(null));
  const [baseline, setBaseline] = useState<ReasoningEditorBuffer>(() => bufferFromRow(null));

  const [pendingKey, setPendingKey] = useState<ReasoningConfigKey | null>(null);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [historyOpen, setHistoryOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: Severity }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const loadConfig = useCallback(() => {
    setLoadingConfig(true);
    setConfigError(null);
    quillApi.reasoning
      .getConfig()
      .then((rows) => setConfigRows(rows))
      .catch((e) => setConfigError(describeReasoningError(e)))
      .finally(() => setLoadingConfig(false));
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  // Re-derive the editor buffer whenever the selected key or the loaded rows
  // change (including after a save/restore refresh).
  useEffect(() => {
    if (loadingConfig) return;
    const row = configRows?.find((r) => r.config_key === selectedKey) || null;
    const fresh = bufferFromRow(row);
    setBuffer(fresh);
    setBaseline(fresh);
  }, [selectedKey, configRows, loadingConfig]);

  const activeRow = configRows?.find((r) => r.config_key === selectedKey) || null;
  const manifestEntry = REASONING_CONFIG_MANIFEST.find((m) => m.key === selectedKey) || REASONING_CONFIG_MANIFEST[0];
  const dirty = isBufferDirty(buffer, baseline);

  const setKeyAndUrl = useCallback(
    (key: ReasoningConfigKey) => {
      setSelectedKey(key);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('key', key);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const doSelectKey = useCallback(
    (key: ReasoningConfigKey) => {
      if (key === selectedKey) return;
      if (dirty) {
        setPendingKey(key);
      } else {
        setKeyAndUrl(key);
      }
    },
    [selectedKey, dirty, setKeyAndUrl]
  );

  const confirmDiscardAndSwitch = () => {
    if (!pendingKey) return;
    setKeyAndUrl(pendingKey);
    setPendingKey(null);
  };

  const handleBufferChange = (patch: Partial<ReasoningEditorBuffer>) => {
    setBuffer((prev) => ({ ...prev, ...patch }));
  };

  const handleRequestSave = () => setSaveConfirmOpen(true);

  const handleSaveConfirm = () => {
    setSaving(true);
    quillApi.reasoning
      .updateConfig(selectedKey, {
        prompt_body: buffer.prompt_body,
        model: buffer.model,
        temperature: buffer.temperature,
        max_tokens: buffer.max_tokens,
        edit_note: buffer.edit_note,
      })
      .then(() => {
        setSaveConfirmOpen(false);
        setSnackbar({ open: true, message: `Saved ${manifestEntry.displayName} — new version is now active.`, severity: 'success' });
        loadConfig();
      })
      .catch((e) => {
        setSnackbar({ open: true, message: describeReasoningError(e), severity: 'error' });
      })
      .finally(() => setSaving(false));
  };

  const nextVersion = (activeRow?.version ?? 0) + 1;
  const saveDescription = activeRow
    ? `Creates version ${nextVersion} and deactivates v${activeRow.version}. The live Quill engine uses this prompt (production).`
    : 'Creates version 1. The live Quill engine uses this prompt (production).';

  return (
    <Box sx={{ p: 3 }}>
      {/* Page header — 48px teal icon tile + title + subtitle, matching sibling Quill pages */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '12px',
            backgroundColor: `${colors.deepTeal[500]}1A`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 2,
            flexShrink: 0,
          }}
        >
          <TuneIcon sx={{ fontSize: 28, color: colors.deepTeal[500] }} />
        </Box>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography sx={{ fontFamily: typography.fonts.display, fontSize: '1.75rem', fontWeight: 700, color: colors.gray[900] }}>
              Reasoning Prompts
            </Typography>
            <HelpTip id="quill-reasoning" />
          </Box>
          <Typography sx={{ fontFamily: typography.fonts.body, fontSize: typography.sizes.sm, color: 'text.secondary' }}>
            Constitutional classifier, refusal generation, and judge prompts — versioned with a live test harness.
          </Typography>
        </Box>
      </Box>

      <Typography variant="caption" sx={{ display: 'block', mb: 3, color: status === 'up' ? 'success.main' : 'text.disabled' }}>
        ● Local backend • {status === 'up' ? 'connected' : status}
      </Typography>

      <Box sx={{ display: 'flex', gap: 4, alignItems: 'flex-start' }}>
        <ConfigKeyList
          configRows={configRows}
          loading={loadingConfig}
          error={configError}
          onRetry={loadConfig}
          selectedKey={selectedKey}
          onSelectKey={doSelectKey}
        />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          {loadingConfig ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <PromptEditor
                configKey={selectedKey}
                displayName={manifestEntry.displayName}
                description={manifestEntry.description}
                activeRow={activeRow}
                buffer={buffer}
                onBufferChange={handleBufferChange}
                isDirty={dirty}
                saving={saving}
                onRequestSave={handleRequestSave}
                onOpenHistory={() => setHistoryOpen(true)}
              />

              <ABTestSandbox configKey={selectedKey} editorPromptBody={buffer.prompt_body} hasActiveVersion={!!activeRow} />
            </>
          )}
        </Box>
      </Box>

      <VersionHistoryDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        configKey={selectedKey}
        displayName={manifestEntry.displayName}
        activeRow={activeRow}
        onRestored={loadConfig}
      />

      <QuillActionConfirm
        open={saveConfirmOpen}
        title="Save new version"
        message={saveDescription}
        confirmLabel="Save version"
        loading={saving}
        onConfirm={handleSaveConfirm}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <QuillActionConfirm
        open={pendingKey != null}
        title="Discard unsaved changes?"
        message={`You have unsaved changes to ${manifestEntry.displayName}. Switching keys will discard them.`}
        confirmLabel="Discard changes"
        destructive
        onConfirm={confirmDiscardAndSwitch}
        onCancel={() => setPendingKey(null)}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReasoningPromptsPage;
